import React, {useState, useRef, useEffect, forwardRef} from 'react';
import usePortal from 'react-useportal';

import {
    Flex,
    Icon,
    PriorityIcon,
    Link,
    Separator,
    DropdownV2,
    ListItem,
    Text
} from '@priority-software/priority-style-react';
import styles from '../styles/SavedReports.module.scss';
import texts from '../texts';
import classNames from 'classnames';
import {
    toggleAllowDeletion,
    deleteSavedReport,
    displaySavedReport,
    sendSavedReport
} from '../actions/savedReports';
import {computeDeleteDropLeft, computeDeleteDropTop} from '../utils/savedReports';
import {SavedReport} from '../../ReportsDialog/types';

const DeleteDropTrigger = forwardRef((props: any, ref: any) => {
    const {isDeleteDropOpen, setIsDeleteDropOpen} = props;
    const triggerClasses = classNames('ignore-react-onclickoutside', {
        [styles.triggerOpen]: isDeleteDropOpen
    });

    return (
        <div ref={ref}>
            <Icon
                componentClasses={triggerClasses}
                icon={PriorityIcon.iconMore}
                iconColor="secondary-400"
                onClick={() => setIsDeleteDropOpen(prevState => !prevState)}
            />
        </div>
    );
});

export interface ISavedReportItemProps {
    dialogZIndex: number;
    closeDialog: () => void;
    item: SavedReport;
    etype: string;
    ename: string;
    onDelete: (idx: number) => void;
    idx: number;
    containerRef: React.MutableRefObject<any>;
}

export const SavedReportItem: React.FC<ISavedReportItemProps> = ({
    dialogZIndex,
    closeDialog,
    item,
    etype,
    ename,
    onDelete,
    idx,
    containerRef
}) => {
    const {title, nodelete} = item;

    const listItemRef = useRef(null);

    const [isHovering, setIsHovering] = useState(false);
    const [isDeleteDropOpen, setIsDeleteDropOpen] = useState(false);
    const [deleteDropLeft, setDeleteDropLeft] = useState(0);
    const [deleteDropTop, setDeleteDropTop] = useState(0);
    const [isNoDelete, setIsNoDelete] = useState(nodelete === '1');

    const commaIdx = title.indexOf(',');
    const data = title.substring(0, commaIdx);
    const desc = title.substring(commaIdx + 1);
    const [date, time, user] = data.split(' ');
    const containerEl = containerRef.current;

    useEffect(() => {
        if (isHovering && containerEl) {
            const containerRect = containerEl.getBoundingClientRect();
            const elementRect = listItemRef.current.getBoundingClientRect();
            setDeleteDropLeft(computeDeleteDropLeft(elementRect, containerRect));
            setDeleteDropTop(computeDeleteDropTop(elementRect, containerRect));
        }
    }, [isHovering, containerEl]);

    const handleNodeleteToggle = async () => {
        const newNoDelete = await toggleAllowDeletion(etype, ename, item.name);
        setIsNoDelete(newNoDelete);
    };

    const handleDelete = async () => {
        await deleteSavedReport(etype, ename, item.name);
        onDelete(idx);
    };

    const handleDisplayReport = async () => {
        await displaySavedReport(etype, ename, item.name);
        closeDialog();
    };

    const handleSendReport = async () => {
        await sendSavedReport(etype, ename, item.name);
        closeDialog();
    };

    const handleItemLeave = () => {
        setIsHovering(false);
        setIsDeleteDropOpen(false);
    };

    const {Portal} = usePortal({bindTo: containerRef.current});
    return (
        <div
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={handleItemLeave}
            onWheel={handleItemLeave}
        >
            <ListItem isActive={false} isFocused={false} componentClasses={styles.item}>
                <Flex justifyContent={'flex-start'}>
                    <Flex justifyContent="flex-start" componentClasses={styles.reportData}>
                        {[date, time, user].map(data => {
                            return (
                                <Text
                                    key={data}
                                    skin="breadcrumb"
                                    textColor="primary-700"
                                    componentClasses={styles.dataItem}
                                >
                                    {data}
                                </Text>
                            );
                        })}
                        <Text skin="breadcrumb" textColor="primary-700">
                            {desc}
                        </Text>
                    </Flex>
                    {isHovering && (
                        <Flex componentClasses={styles.buttons} justifyContent={'flex-start'}>
                            <Link
                                componentClasses={styles.linkButton}
                                onClick={handleDisplayReport}
                            >
                                {texts.display}
                            </Link>
                            <Separator align="horizontal" height={20} />
                            <Link componentClasses={styles.linkButton} onClick={handleSendReport}>
                                {texts.send}
                            </Link>
                            {
                                <DeleteDropTrigger
                                    ref={listItemRef}
                                    setIsDeleteDropOpen={setIsDeleteDropOpen}
                                />
                            }
                            {isDeleteDropOpen && (
                                <Portal>
                                    <DropdownV2
                                        trigger={
                                            <DeleteDropTrigger
                                                isDeleteDropOpen={isDeleteDropOpen}
                                                setIsDeleteDropOpen={setIsDeleteDropOpen}
                                            />
                                        }
                                        isOpen
                                        openingSide={'leftSide'}
                                        contentStyle={{margin: 0, width: '190px'}}
                                        componentClasses={styles.deleteDrop}
                                        style={{
                                            zIndex: dialogZIndex + 1,
                                            left: deleteDropLeft,
                                            top: deleteDropTop
                                        }}
                                    >
                                        <ListItem
                                            isActive={false}
                                            isFocused={false}
                                            onClick={handleNodeleteToggle}
                                        >
                                            {isNoDelete ? texts.allowDeletion : texts.blockDeletion}
                                        </ListItem>
                                        {!isNoDelete && (
                                            <ListItem
                                                isActive={false}
                                                isFocused={false}
                                                onClick={handleDelete}
                                            >
                                                {texts.delete}
                                            </ListItem>
                                        )}
                                    </DropdownV2>
                                </Portal>
                            )}
                        </Flex>
                    )}
                </Flex>
            </ListItem>
        </div>
    );
};
